/**
 * Account Review Dialog
 *
 * Lists accounts requiring user attention (re-auth, scope upgrade).
 * Shown when multiple accounts have errors simultaneously.
 * Invocable from anywhere via useAccountReviewDialog hook.
 *
 * @see .work/specs/M9/005-widget-platform-integration.md
 */
'use client';

import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@appgnostic/design-system';
import { Link2 } from 'lucide-react';
import { useTranslations } from 'next-intl';
import {
  createContext,
  useCallback,
  useContext,
  useState,
  type ReactNode,
} from 'react';

import type { PlatformError } from '../../context';
import { useReconnect } from '../../hooks';

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------

interface AccountReviewDialogState {
  isOpen: boolean;
  errors: PlatformError[];
}

interface AccountReviewDialogContextValue {
  state: AccountReviewDialogState;
  openAccountReview: (errors: PlatformError[]) => void;
  close: () => void;
}

// -----------------------------------------------------------------------------
// Context
// -----------------------------------------------------------------------------

const AccountReviewDialogContext =
  createContext<AccountReviewDialogContextValue | null>(null);
AccountReviewDialogContext.displayName = 'AccountReviewDialogContext';

const initialState: AccountReviewDialogState = {
  isOpen: false,
  errors: [],
};

// -----------------------------------------------------------------------------
// Provider
// -----------------------------------------------------------------------------

interface AccountReviewDialogProviderProps {
  children: ReactNode;
}

/**
 * Provider for the Account Review Dialog.
 *
 * Mount inside PlatformErrorProvider in the app Providers.
 *
 * @example
 * <PlatformErrorProvider>
 *   <AccountReviewDialogProvider>
 *     {children}
 *   </AccountReviewDialogProvider>
 * </PlatformErrorProvider>
 */
export function AccountReviewDialogProvider({
  children,
}: AccountReviewDialogProviderProps) {
  const [state, setState] = useState<AccountReviewDialogState>(initialState);

  const openAccountReview = useCallback((errors: PlatformError[]) => {
    setState({
      isOpen: true,
      errors,
    });
  }, []);

  const close = useCallback(() => {
    setState(initialState);
  }, []);

  return (
    <AccountReviewDialogContext.Provider
      value={{ state, openAccountReview, close }}
    >
      {children}
      <AccountReviewDialogContent />
    </AccountReviewDialogContext.Provider>
  );
}

// -----------------------------------------------------------------------------
// Hook
// -----------------------------------------------------------------------------

/**
 * Hook to open the Account Review Dialog.
 *
 * @throws Error if used outside AccountReviewDialogProvider
 *
 * @example
 * function MultipleErrorBanner({ errors }) {
 *   const { openAccountReview } = useAccountReviewDialog();
 *
 *   return (
 *     <Button onClick={() => openAccountReview(errors)}>
 *       Review Accounts
 *     </Button>
 *   );
 * }
 */
export function useAccountReviewDialog() {
  const ctx = useContext(AccountReviewDialogContext);

  if (!ctx) {
    throw new Error(
      'useAccountReviewDialog must be used within AccountReviewDialogProvider. ' +
        'Ensure your component is rendered inside the app Providers.'
    );
  }

  return {
    openAccountReview: ctx.openAccountReview,
  };
}

// -----------------------------------------------------------------------------
// Dialog Content
// -----------------------------------------------------------------------------

function AccountReviewDialogContent() {
  const ctx = useContext(AccountReviewDialogContext);
  const t = useTranslations('integrations.dialogs.accountReview');
  const tCommon = useTranslations('common');

  if (!ctx) return null;

  const { state, close } = ctx;
  const { isOpen, errors } = state;

  return (
    <Dialog open={isOpen} onOpenChange={open => !open && close()}>
      <DialogContent className="max-w-md" closeLabel={tCommon('close')}>
        <DialogHeader>
          <DialogTitle>{t('title')}</DialogTitle>
          <DialogDescription>
            {t('description', { count: errors.length })}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-4">
          {errors.map(error => (
            <AccountErrorRow
              key={error.accountId}
              error={error}
              onSuccess={close}
            />
          ))}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={close}>
            {tCommon('close')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// -----------------------------------------------------------------------------
// Account Error Row
// -----------------------------------------------------------------------------

interface AccountErrorRowProps {
  error: PlatformError;
  onSuccess: () => void;
}

function AccountErrorRow({ error, onSuccess }: AccountErrorRowProps) {
  const tCommon = useTranslations('common');
  const { reconnect, isReconnecting, reconnectingAccountId } = useReconnect();
  const isThisAccountReconnecting = reconnectingAccountId === error.accountId;

  const handleReconnect = async () => {
    try {
      await reconnect({
        accountId: error.accountId,
        provider: error.provider as 'github',
      });
      onSuccess();
    } catch {
      // Error handled by reconnect hook
    }
  };

  // Map error userAction to user-friendly message
  const getErrorMessage = () => {
    const userAction = error.error.userAction;
    switch (userAction) {
      case 're-authenticate':
        return 'Connection expired';
      case 'upgrade':
        return 'Additional permissions needed';
      case 'contact-developer':
        return 'Please contact support';
      case 'retry':
        return 'Temporary issue';
      case 'fix-input':
        return 'Invalid configuration';
      default:
        return 'Connection issue';
    }
  };

  return (
    <div className="flex items-center justify-between rounded-lg border p-3">
      <div className="flex items-center gap-3">
        <div className="bg-muted flex h-8 w-8 items-center justify-center rounded-full">
          <Link2 className="h-4 w-4" />
        </div>
        <div>
          <p className="text-sm font-medium">{error.accountName}</p>
          <p className="text-muted-foreground text-xs">{getErrorMessage()}</p>
        </div>
      </div>
      <Button
        size="sm"
        onClick={handleReconnect}
        disabled={isReconnecting || isThisAccountReconnecting}
      >
        {isThisAccountReconnecting
          ? tCommon('connecting')
          : tCommon('reconnect')}
      </Button>
    </div>
  );
}
